import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePartyApi } from "../api/party-api";
import type {
  ChangePasswordInput,
  CustomerProfile,
  SetLoginOtpInput,
  UpdateProfileInput,
} from "../api/contracts";

// Profil / Hesabım — the party self-service writes (ADR 0005 §5 / WEB3). Both are plain
// @PartyOnly writes on the caller's OWN row (the server resolves the party from the
// verified customer_sub — no id is trusted, none is sent). Each echoes the full profile
// and invalidates the shared ["customer","profile"] query so the shell + surface refetch
// the fresh masked profile. No fresh-auth gate here (unlike KVKK consent): name/email and
// the login-OTP preference are not the possession factor.
const PROFILE_KEY = ["customer", "profile"];

/** PATCH /v1/customer/profile — edit NAME + email (self scope). Phone/TCKN are not in the
 *  editable set; the schema is `.strict()` and rejects them at the boundary. */
export function useUpdateProfile() {
  const api = usePartyApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) =>
      api.patch<CustomerProfile>("/v1/customer/profile", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
}

/** POST /v1/customer/change-password — the first-class customer self password-change.
 *  The server verifies `currentPassword` server-side before setting `newPassword` (min 6);
 *  a wrong current password is a clean 400 (ProblemJson), Keycloak-unconfigured a 503. No
 *  query invalidation — nothing in the masked profile changes. */
export function useChangePassword() {
  const api = usePartyApi();
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      api.post<{ changed: true }>("/v1/customer/change-password", input),
  });
}

/** PATCH /v1/customer/settings/login-otp — toggle the RETURNING-login OTP requirement.
 *  The first-registration possession factor is unaffected (always mandatory). */
export function useSetLoginOtp() {
  const api = usePartyApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetLoginOtpInput) =>
      api.patch<{ loginOtpDisabled: boolean }>("/v1/customer/settings/login-otp", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PROFILE_KEY }),
  });
}
